import Store from '@ember-data/store';
import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import type { ContactSaveNewPropertyArgs } from 'donor-elf-web/components/contact/custom-property/add/new';
import ContactModel from 'donor-elf-web/models/contact';
import ContactsCache, {
  type ContactCustomPropertyInfo,
} from 'donor-elf-web/services/contacts-cache';

interface ComponentArgs {
  contact: ContactModel;
  existingPropertyInfos: ContactCustomPropertyInfo[]; // can manually pass in the available existing properties to show
  newProperty?: boolean; // default false. If true, go directly to new property form
  afterSave: () => void;
  onCancel: () => void;
}

export default class ContactDetailsPropertiesCustomPropertyAddIndexComponent extends Component<ComponentArgs> {
  constructor(owner: any, args: ComponentArgs) {
    super(owner, args);
    if (
      this.existingPropertyInfosNotOnContact.length === 0 ||
      this.args.newProperty === true
    ) {
      this.newProperty = true;
    }
  }

  @service declare contactsCache: ContactsCache;
  @service declare store: Store;

  @tracked existingPropertyLabel?: string;
  @tracked newProperty = false;

  get existingLabelsOnContact(): { [label: string]: true } {
    return (this.args.contact.custom?.properties || []).reduce(
      (obj: any, x) => ((obj[x.l.toLowerCase()] = true), obj),
      {},
    );
  }

  get existingPropertyInfosNotOnContact(): ContactCustomPropertyInfo[] {
    let { existingLabelsOnContact } = this;
    return (
      this.args.existingPropertyInfos ||
      this.contactsCache.customProperties.filter(
        (x) =>
          x.r !== true && existingLabelsOnContact[x.l.toLowerCase()] !== true,
      )
    );
  }

  get existingPropertyInfo(): ContactCustomPropertyInfo | undefined {
    return this.contactsCache.customPropertiesByLowecaseLabel[
      this.existingPropertyLabel?.toLowerCase() || ''
    ];
  }

  @action
  handleNewPropertySelected() {
    this.newProperty = true;
  }

  @action
  handleExistingPropertySelected(info: ContactCustomPropertyInfo) {
    this.existingPropertyLabel = info.l;
  }

  @action
  async handleNewPropertyOnSave(
    saveArgs: ContactSaveNewPropertyArgs,
  ): Promise<void> {
    let saveCustomProperty = this.store.createRecord('custom-property', {
      contactId: this.args.contact.id,
      l: saveArgs.l,
      v: saveArgs.v,
      t: saveArgs.t,
      o: saveArgs.o,
      s: saveArgs.s,
    });
    await saveCustomProperty.save();
    if (saveCustomProperty.customPropertyInfo != null) {
      this.contactsCache.updateCustomPropertyInfo(
        saveCustomProperty.customPropertyInfo,
      );
    }
    await this.args.contact.reload();
    this.args.afterSave();
  }
}
